import type { KnowledgeWeaveView } from '@aztec-realms/shared-types';

export function WeaveRouteNotice({
  kind,
  attribution,
  datasetRelease,
}: {
  kind?: string;
  attribution?: string;
  datasetRelease?: string;
}) {
  const publicCorridor = kind === 'public_corridor';
  return (
    <div className="explore-weave-provenance" data-route-kind={kind ?? 'direct_preview'}>
      <span>
        {publicCorridor ? 'Walking connection' : 'Connection preview · choose a safe walking route'}
      </span>
      {publicCorridor && attribution && (
        <details className="explore-weave-source">
          <summary>Route source</summary>
          <small>
            {attribution}
            {datasetRelease ? ` · ${datasetRelease}` : ''}
          </small>
        </details>
      )}
    </div>
  );
}

/** Progress is projected only from the server's binding readiness, never GPS proximity. */
export function WeaveProgress({ weave }: { weave: KnowledgeWeaveView }) {
  const complete = weave.status === 'awakened' || weave.challengeState === 'completed';
  const visitsVerified =
    complete || weave.challengeState === 'ready' || weave.challengeState === 'active';
  const unavailable = weave.challengeState === 'unavailable';
  const originVisited =
    visitsVerified || weave.visitStage === 'destination_required' || weave.visitStage === 'arrived';
  const destinationVisited = visitsVerified || weave.visitStage === 'arrived';
  const steps = [
    {
      title: weave.sourceName,
      description: originVisited ? 'Origin visit confirmed.' : 'Visit the origin in your new walk.',
      done: originVisited,
      current: !originVisited && !unavailable,
    },
    {
      title: weave.destinationName,
      description: destinationVisited
        ? 'Destination visit confirmed.'
        : 'Then visit the destination.',
      done: destinationVisited,
      current: originVisited && !destinationVisited && !unavailable,
    },
    {
      title: 'Connection puzzle',
      description: complete
        ? 'Your current is awakened.'
        : weave.challengeState === 'active'
          ? 'Continue your saved binding challenge.'
          : visitsVerified
            ? 'Your binding challenge is ready.'
            : 'Opens after your route is verified.',
      done: complete,
      current: visitsVerified && !complete && !unavailable,
    },
  ];

  return (
    <ol className="explore-weave-progress" aria-label="Weave progress">
      {steps.map((step, index) => (
        <li
          key={step.title}
          data-complete={step.done || undefined}
          aria-current={step.current ? 'step' : undefined}
        >
          <span className="explore-weave-progress__number" aria-hidden="true">
            {step.done ? '✓' : index + 1}
          </span>
          <span>
            <strong>
              {step.title}
              {step.done ? ' · complete' : ''}
            </strong>
            <small>{step.description}</small>
          </span>
        </li>
      ))}
    </ol>
  );
}
